import { BufferEncoding } from '@hdms/shared/common-types';

import { isString } from './type-utils';

/**
 * Convert string in camelCase to kebap-case
 * @param string
 */
export function camelToKebapCase(string: string): string {
  return (string || '').replace(/[a-z][A-Z]/g, str => `${str[0]}-${str[1]}`).toLowerCase();
}

export function kebapToCamelCase(string: string): string {
  return (string || '').replace(/-\w?/g, str => (str[1] || '').toUpperCase());
}

/**
 * Safely parse a string as JSON.
 * If parsing fails it'll return the original value
 * @param val
 */
export function parseJsonString<T = unknown>(val: string): T {
  try {
    return JSON.parse(val);
  } catch (e) {
    return val as T;
  }
}

/**
 * Removes all HTML tags and double quotes and returns a string
 *
 * @param text Description to be sanitized
 * @return string "sanitized" text for safe usage in html tags attributes (title, meta tags).
 */
export function sanitizeText<T>(text: T): T {
  return isString(text) ? (text.replace(/<\/?([a-z]+[^>]*)>/gi, '').replace(/\"/g, '') as T) : text;
}

/**
 * Node/Browser compatible version of atob
 */
export const safeAtob =
  typeof Buffer !== 'function'
    ? window.atob
    : (str: string) => Buffer.from(str, BufferEncoding.BASE64).toString(BufferEncoding.BINARY);

/**
 * Node/Browser compatible version of btoa
 */
export const safeBtoa =
  typeof Buffer !== 'function'
    ? window.btoa
    : (str: string) => Buffer.from(str, BufferEncoding.BINARY).toString(BufferEncoding.BASE64);

/**
 * Encode from Unicode to base64.
 * Replacement for deprecated `unescape`, required for converting utf-8 to iso-8859-1.
 * First use encodeURIComponent to get percent-encoded UTF-8,
 * then convert the percent encodings into raw bytes which
 * can be fed into btoa.
 * @param str
 */
export function b64EncodeUnicode(str: string): string {
  return safeBtoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))));
}

/**
 * Decode from base64 to Unicode.
 * Replacement for deprecated `escape`, required for converting iso-8859-1 to utf-8.
 * Going backwards: from bytestream, to percent-encoding, to original string.
 * @param str
 */
export function b64DecodeUnicode(str: string): string {
  return decodeURIComponent(
    safeAtob(str)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
}

/**
 * Checks phone number to ensure country code is properly appended before submitting to SAP
 *
 * @param phoneValue This can come from Hybris or Universal country config
 * @return string value with appended plus sign and country code with no duplication.
 */

export function sanitizeAndUpdatePhoneNumber(countryDialCode: string, phoneValue: string): string {
  if (phoneValue) {
    const hasCountryCode = /^\+/.test(phoneValue);
    if (!hasCountryCode) {
      if (phoneValue.slice(0, 2) === '00') {
        phoneValue = phoneValue.replace('00', '+');
      } else {
        phoneValue = countryDialCode + phoneValue;
      }
    }
    return phoneValue.replace(/[^0-9+]/g, '');
  } else {
    return null;
  }
}

/**
 * Reverses a given string
 * @example
 * ```ts
 * console.log(reverse('hello'));
 * // Prints 'olleh'
 * ```
 * @param str
 */
export const reverseString = (str: string) => (str ? [...str].reverse().join('') : str);
